import {useVorderState} from "@/modules/shop/store/vorder-state";
import {defineStore, storeToRefs} from "pinia";
import {computed} from "vue";
import {useCatalogStore} from "@/modules/shop/store/catalog";
import {useFortuneStore} from "@/modules/fortune/store/fortune";
import {FortunePrize} from "@/gql/gen";

const STORE_NAME = 'vorder-prize'

/**
 * Composable для управления выбором приза в заказе
 *
 * Логика работы:
 * 1. Когда сервер возвращает приз, устанавливаем PRIZE_ID
 * 2. Выбранный приз может быть удален пользователем
 * 3. PRIZE_ID синхронизируется с fortuneStore.userPrizeElement
 */
export const useVorderPrize = defineStore(STORE_NAME, () => {

    const vorderState = useVorderState();
    const catalogStore = useCatalogStore()
    const fortuneStore = useFortuneStore();

    const {userPrizeElement: prize} = storeToRefs(fortuneStore);
    const {attrs} = storeToRefs(vorderState);

    /**
     * Проверяет доступен ли приз с указанным ID
     */
    const isPrizeAvailable = (prizeId: number | null | undefined): boolean => {
        if (!prizeId || prizeId <= 0) return false;
        return prize.value?.ID === prizeId;
    };

    /**
     * Текущий выбранный приз (если доступен)
     */
    const selectedPrize = computed(() => {
        const prizeId = attrs.value.PRIZE_ID;
        if (prizeId && prizeId > 0 && isPrizeAvailable(prizeId)) {
            return prize.value;
        }
        return null;
    });

    /**
     * Доступен ли приз для использования
     */
    const hasPrize = computed(() => !!prize.value);

    /**
     * Активирован ли приз в текущем заказе
     */
    const isPrizeActive = computed(() => {
        const prizeId = attrs.value.PRIZE_ID;
        return !!prizeId && prizeId > 0 && isPrizeAvailable(prizeId);
    });

    /**
     * Устанавливает приз для использования в заказе
     */
    const activatePrize = () => {
        if (!prize.value) return;
        vorderState.setAttr('PRIZE_ID', prize.value.ID);
    };

    /**
     * Удаляет приз из заказа (только локально, без вызова API)
     */
    const removePrizeLocal = () => {
        vorderState.setAttr('PRIZE_ID', null);
    };

    /**
     * Автоматическая синхронизация PRIZE_ID при изменении приз
     */
    const syncPrizeId = () => {
        const currentPrizeId = attrs.value.PRIZE_ID;
        const availablePrize = prize.value;

        // Если есть доступный приза
        if (availablePrize) {
            // Если PRIZE_ID не установлен или установлен неверно
            if (!currentPrizeId || currentPrizeId <= 0 || !isPrizeAvailable(currentPrizeId)) {
                // Устанавливаем ID доступного приза
                vorderState.setAttr('PRIZE_ID', availablePrize.ID);
            }
        } else {
            // Если приза нет, сбрасываем PRIZE_ID
            if (currentPrizeId && currentPrizeId > 0) {
                vorderState.setAttr('PRIZE_ID', null);
            }
        }
    };

    const prizeProduct = computed(() => {
        if (!prize.value?.GIFT_ELEMENT) return null
        return catalogStore.productsById[prize.value?.GIFT_ELEMENT] || null
    })

    const selectPrize = (prizeEntity?: FortunePrize) => {
        prizeEntity = prizeEntity || prize.value || undefined;
        if (prizeEntity) {
            vorderState.setAttr('PRIZE_ID', prizeEntity.ID);
        }
    }

    return {
        // State
        selectedPrize,
        hasPrize,
        isPrizeActive,
        prizeProduct,
        prize,

        // Actions
        selectPrize,
        activatePrize,
        removePrizeLocal,
        syncPrizeId,
        isPrizeAvailable,
    };
})
